import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';

// Personel Statüsü tanımları (AKM POC §6.1) — titles/config aynası.
// Backend LegalFramework enum'u SAYI olarak serileşir: 0 = 4857 İş Kanunu, 1 = 657 Devlet Memurları Kanunu.
export const LEGAL_FRAMEWORK_KEYS = {
  0: 'employeeStatus.labor4857',
  1: 'employeeStatus.civilServant657',
};

export const legalFrameworkLabel = (value, t) => {
  const key = LEGAL_FRAMEWORK_KEYS[value];
  return key ? t(key) : '-';
};

// Form değerleri gövdeye: sortOrder TextInput'tan string gelir; boş string int alana gönderilirse
// System.Text.Json tüm gövdeyi reddeder.
export const toEmployeeStatusPayload = (formData) => {
  const sort = formData?.sortOrder;
  const parsedSort = sort === '' || sort === null || sort === undefined ? 0 : parseInt(sort, 10);
  const lf = formData?.legalFramework;
  return {
    name: (formData?.name || '').trim(),
    code: formData?.code ? String(formData.code).trim() : null,
    legalFramework: lf === '' || lf === null || lf === undefined ? 0 : Number(lf),
    sortOrder: Number.isNaN(parsedSort) ? 0 : parsedSort,
  };
};

const useEmployeeStatusApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    // api.js GET'te REDDETMEZ: hata sinyali res.data.status (.catch ölü kod olurdu).
    api.get(`/v1/employee-statuses?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        if (res?.data?.status !== true) {
          setLoadFailed(true);
          setData([]);
          setPagination({ totalRowCount: 0, pageSize: paramsToUse.pageSize || 10, pageIndex: 0 });
          return;
        }
        setLoadFailed(false);
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/employee-statuses', toEmployeeStatusPayload(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/employee-statuses/${id}`, toEmployeeStatusPayload(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  // Dönüş: { ok, skippedCount }. Tekil silmede aktif çalışana bağlı statü 409 employeeStatus.inUse
  // ile reddedilir (toast interceptor'da) → ok:false, tablo YENİLENMEZ. Toplu silmede sunucu
  // kullanımdaki statüleri atlar ve skippedIds döner — sayfa bunu satır içi notla söyler.
  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/employee-statuses/${ids[0]}`);
        setRefreshTrigger((p) => p + 1);
        return { ok: true, skippedCount: 0 };
      }
      const res = await api.post('/v1/employee-statuses/batch-delete', ids);
      const skipped = res?.data?.data?.skippedIds;
      setRefreshTrigger((p) => p + 1);
      return { ok: true, skippedCount: Array.isArray(skipped) ? skipped.length : 0 };
    } catch {
      return { ok: false, skippedCount: 0 };
    }
  }, []);

  const legalFrameworkOptions = useMemo(() => [
    { id: 0, name: t('employeeStatus.labor4857') },
    { id: 1, name: t('employeeStatus.civilServant657') },
  ], [t]);

  // Uç yalnız `name[like]` süzgecini ve sabit sıralamayı (SortOrder, Name) tanır: diğer sütunlarda
  // filtre/sıralama oku gösterilmez (sahte süzgeç olurdu).
  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('employeeStatus.name'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'code', label: t('employeeStatus.code'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
    {
      name: 'legalFramework',
      label: t('employeeStatus.legalFramework'),
      options: { filter: false, sort: false, customBodyRender: (v) => legalFrameworkLabel(v, t) },
    },
    { name: 'sortOrder', label: t('employeeStatus.sortOrder'), options: { filter: false, sort: false } },
    { name: 'employeeCount', label: t('employeeStatus.employeeCount'), options: { filter: false, sort: false } },
  ], [t]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('employeeStatus.name'), type: 'text', grid: 12, required: true, maxLength: 100 },
    { name: 'code', label: t('employeeStatus.code'), type: 'text', grid: 6, maxLength: 30 },
    { name: 'sortOrder', label: t('employeeStatus.sortOrder'), type: 'number', grid: 6, min: 0, defaultValue: 0 },
    {
      name: 'legalFramework', label: t('employeeStatus.legalFramework'), type: 'select', grid: 12, required: true,
      options: legalFrameworkOptions, idKey: 'id', labelKey: 'name', defaultValue: 0,
    },
  ], [t, legalFrameworkOptions]);

  return { objectSchema, columns, data, fetching, loadFailed, pagination, create, update, _delete };
};

export default useEmployeeStatusApi;
